import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query'
import * as Sentry from '@sentry/browser'
import axios, { AxiosRequestConfig, Method } from 'axios'
import qs from 'qs'

import { API_URL } from '../const'
import { errorMessage } from './errorMessage'
import { jwt } from './index'
import { serializeGetParams } from './url'

const HAS_NO_PROFILE = 'Отсутствует профиль пользователя'
const HAS_NO_PROFILE_EN = 'The user profile is missing'

let isAlreadyFetchingAccessToken = false
let subscribers: any = []
let refreshCounts = 0

function onAccessTokenFetched(accessToken: string) {
  subscribers = subscribers.filter((callback: any) => callback(accessToken))
}

function addSubscriber(callback: any) {
  subscribers.push(callback)
}

axios.interceptors.response.use(
  (response) => {
    if (response.config && response.config.url && !response.config.url.includes('token-refresh')) {
      refreshCounts = 0
    }

    return response
  },
  (error) => {
    const { config, response } = error
    const originalRequest = config

    // logging when expired token loops
    if (refreshCounts > 1) {
      Sentry.withScope(() => {
        Sentry.setExtra('response', response)

        Sentry.captureMessage(`Зацикливание запроса`, Sentry.Severity.Info)
      })
    }

    if (
      (originalRequest.url.includes('token-refresh') && response && response.status === 401) ||
      // experiment with expired token loops
      refreshCounts > 1 ||
      (response &&
        response.status === 403 &&
        response.data &&
        response.data.detail &&
        (response.data.detail === HAS_NO_PROFILE || response.data.detail === HAS_NO_PROFILE_EN))
    ) {
      jwt.clear()
      document.location.reload()
    }

    if (
      response &&
      response.status === 401 &&
      ((response.data && response.data.code && response.data.code === 'token_not_valid') ||
        (response.config && response.config.responseType === 'arraybuffer'))
    ) {
      if (!isAlreadyFetchingAccessToken) {
        isAlreadyFetchingAccessToken = true
        jwt
          .refreshToken()
          .then((resp: any) => {
            if (resp.status === 200) {
              refreshCounts++
              isAlreadyFetchingAccessToken = false
              onAccessTokenFetched(jwt.get().accessToken)

              return resp
            }

            return Promise.reject(resp)
          })
          .catch((refreshError: any) => {
            Sentry.configureScope((scope: Sentry.Scope) => {
              scope.setExtra('response', refreshError && refreshError.response)
            })

            Promise.reject(refreshError)
          })
      }

      const retryOriginalRequest = new Promise((resolve) => {
        addSubscriber(() => {
          originalRequest.headers.Authorization = 'Bearer ' + jwt.get().accessToken
          resolve(axios(originalRequest))
        })
      })

      return retryOriginalRequest
    }

    return Promise.reject(error)
  }
)

export default function customAxios(
  url: string,
  method: Method,
  data: any,
  needAuth = true,
  headers: any = {},
  responseType = ''
) {
  if (!headers['Content-type']) {
    headers['Content-type'] = 'application/json'
  }

  const tokens = jwt.get()

  if (needAuth) {
    headers.Authorization = `Bearer ${tokens.accessToken}`
  }

  const config: any = {
    data,
    headers,
    method,
    url,
  }

  if (responseType) {
    config.responseType = responseType
  }

  return axios(config)
}

// Совместим с `RTK Query`
export const customBaseQueryRtk: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args) => {
  try {
    let url: string
    let method: Method = 'GET'
    let body: any = undefined
    let headers: Record<string, string> = {}
    let params: object = {}

    if (typeof args === 'string') {
      url = args
    } else {
      url = args.url
      method = (args.method as Method) || 'GET'
      body = args.body
      headers = (args.headers as Record<string, string>) || {}
      params = args.params || {}
    }

    url = `${API_URL}/${url}`

    if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/json'
    }

    const tokens = jwt.get()

    headers.Authorization = `Bearer ${tokens.accessToken}`

    const config: AxiosRequestConfig = {
      url,
      method,
      data: body,
      headers,
      params,
      paramsSerializer: serializeGetParams,
    }

    const response = await axios(config)
    return { data: response.data }
  } catch (error) {
    return {
      error: {
        status: axios.isAxiosError(error) ? error.response?.status || 500 : 500,
        data: errorMessage(error),
      },
    }
  }
}

export async function GET(url: string, params: object = {}, config: AxiosRequestConfig = {}): Promise<unknown> {
  const response = await customAxios(url, 'get', {
    params,
    paramsSerializer: qs.stringify,
    ...config,
  })

  return response.data
}
